import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useMemo, useRef } from "react"
import { SignContractWizardContext } from "src/main/Contracts/SignContract/SignContractWizardContext"

import AlertBanner from "src/components/AlertBanner"
import Button from "src/components/Button"
import Form from "src/components/Form"

import {
  cancelPaymentMethodSetup as cancelAccountPaymentMethodSetup,
  initializePaymentMethodSetup as initializeAccountPaymentMethodSetup,
} from "src/api/Account/PaymentMethods"
import {
  cancelPaymentMethodSetup as cancelGuestPaymentMethodSetup,
  initializePaymentMethodSetup as initializeGuestPaymentMethodSetup,
} from "src/api/ContractQuotes/PaymentMethods"

import useSetupIntent from "src/hooks/payment_method_hooks/use_setup_intent"
import useStripeForm from "src/hooks/payment_method_hooks/use_stripe_form"

import { stripePromise } from "src/utils/StripePromise"

const AddMethodForm = ({ kind, methodAddedRef }) => {
  const { quote, isGuest, wizardCopy } = useContext(SignContractWizardContext)

  const initFn = useMemo(
    () =>
      isGuest
        ? initializeGuestPaymentMethodSetup(quote.encodedId)
        : initializeAccountPaymentMethodSetup,
    [isGuest, quote.encodedId]
  )
  const cancelFn = useMemo(
    () =>
      isGuest
        ? cancelGuestPaymentMethodSetup(quote.encodedId)
        : cancelAccountPaymentMethodSetup,
    [isGuest, quote.encodedId]
  )

  const { activeSetupIntent, error } = useSetupIntent(
    true,
    [kind],
    false,
    initFn,
    cancelFn
  )

  const activeSetupIntentRef = useRef(activeSetupIntent)
  activeSetupIntentRef.current = activeSetupIntent

  useEffect(() => {
    methodAddedRef.current = false

    return () => {
      const abandoned = activeSetupIntentRef.current
      if (abandoned && !methodAddedRef.current) {
        cancelFn(abandoned.stripeSetupIntentId)
      }
    }
  }, [cancelFn, methodAddedRef])

  if (error) {
    return (
      <AlertBanner type="error">
        {wizardCopy.paymentSetupUnavailable}
      </AlertBanner>
    )
  }

  if (!activeSetupIntent) return null

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret: activeSetupIntent.clientSecret }}
    >
      <StripeSetupForm saveLabel={wizardCopy.save} />
    </Elements>
  )
}

AddMethodForm.propTypes = {
  kind: PropTypes.oneOf(["card", "us_bank_account"]).isRequired,
  methodAddedRef: PropTypes.shape({ current: PropTypes.bool }).isRequired,
}

const StripeSetupForm = ({ saveLabel }) => {
  const stripe = useStripe()
  const elements = useElements()
  const { handleSubmit, handleChange, isSubmitting, error, complete } =
    useStripeForm({ stripe, elements })

  return (
    <form className="flex flex-col space-y-4">
      <PaymentElement options={{ layout: "tabs" }} onChange={handleChange} />
      {complete && error && <Form.Error>{error.message}</Form.Error>}
      <Button
        type="submit"
        variant="secondary"
        disabled={!complete || Boolean(error)}
        onClick={handleSubmit}
        isLoading={isSubmitting}
        fullWidth
      >
        {saveLabel}
      </Button>
    </form>
  )
}

StripeSetupForm.propTypes = {
  saveLabel: PropTypes.string.isRequired,
}

export default AddMethodForm
